import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { v4 as uuid } from 'uuid';

import startCase from 'lodash/startCase';

import Button from 'shared/button';
import ContactSelector from 'bundles/backoffice/contacts/selector';
import ManagedFormGroup from 'components/form/managed_input/form_group';
import ManagedInput from 'components/form/managed_input';
import Modal from 'components/modal';
import SharedAttributeConfirmation from './shared_confirmation';
import { getSharableAttributes } from './helper';
import { scrollToRef } from 'helpers/scrolling';

const defaultState = {
  askForEmployer: false,
  confirmContactSharing: false,
  confirmEmployerSharing: false,
  contact: null,
  contactSharedAttributes: [],
  employer: null,
  employerSharedAttributes: [],
  relationship: null,
};

// TODO: This component got nasty, especially with the outside commission modal (ab)using, it via
//       the `required` prop and the optionality of the `availableContactRelationships` prop.
//       Refactor after snapshot deletion, which should clean up a lot of props and conditionals.
export class GenericContactModal extends React.Component {
  static propTypes = {
    addContactRole: PropTypes.func,
    allowTagging: PropTypes.bool,
    askForEmployer: PropTypes.bool,
    availableContactRelationships: PropTypes.array,
    children: PropTypes.node,
    confirmSharedAttributes: PropTypes.bool,
    contactLabel: PropTypes.string,
    invalid: PropTypes.bool,
    multipleBusinesses: PropTypes.bool,
    newContactVisibility: PropTypes.string,
    parentLabel: PropTypes.string,
    primaryButtonLabel: PropTypes.string,
    requestPermissions: PropTypes.bool,
    required: PropTypes.bool,
    show: PropTypes.bool,
    onClose: PropTypes.func,
    onShow: PropTypes.func,
  }

  roleRef = React.createRef();

  static defaultProps = {
    contactLabel: 'contact',
    parentLabel: 'Role',
    required: true,
  }

  state = defaultState;

  componentDidUpdate(previousProps) {
    if (!previousProps.show && this.props.show) {
      this.setState({
        ...defaultState,
        relationship: this.props.availableContactRelationships?.length === 1
          ? this.props.availableContactRelationships[0]
          : null,
      });

      if (this.props.onShow) {
        this.props.onShow();
      }
    }
  }

  validateNewContactFormSections = () => {
    if (this.props.askForEmployer || !this.props.availableContactRelationships) {
      return false;
    }

    if (!this.state.relationship) {
      this.setState({ missingRelationship: true }, () => scrollToRef(this.roleRef));

      // Return an empty array which is truthy, but deal with error rendering ourselves
      return [];
    }
  }

  handleContactChange = (contact, isNewContact) => {
    this.setState({ contact }, () => {
      if (isNewContact && !this.props.askForEmployer) {
        this.handleSave();
      }
    });

    if (this.props.askForEmployer) {
      this.setState({
        askForEmployer: contact?.isPerson,
        employer: contact?.business,
        searchForEmployer: contact && contact.businessRecords.length === 0,
      });
    }
  }

  showSharedAttributeConfirmationForContact = (contact) => {
    if (!this.props.confirmSharedAttributes || !contact?.private) {
      return false;
    }

    return getSharableAttributes(contact).length > 0;
  }

  handleRelationshipChange = (relationship) => {
    this.setState({ relationship, missingRelationship: false });
  }

  handleSave = () => {
    if (!this.props.required && !this.state.contact) {
      this.props.addContactRole();
      this.props.onClose();
      return;
    }

    const { contact, relationship } = this.state;

    const contactRole = {
      contact,
      employer: this.state.employer,
      // TODO: There is still a mix of relationship and role naming throughout apex/backoffice
      //       Clean up to only use role everywhere
      relationship: relationship?.value,
      relationshipLabel: relationship?.label,
      role: relationship?.value,
      roleLabel: relationship?.label,
    };

    if (!contact.id) {
      contact.localId = uuid();
    }

    if (!this.state.confirmContactSharing && this.showSharedAttributeConfirmationForContact(contact)) {
      this.setState({ confirmContactSharing: true });
      return;
    }

    if (!this.state.confirmEmployerSharing && this.showSharedAttributeConfirmationForContact(this.state.employer)) {
      this.setState({ confirmEmployerSharing: true });
      return;
    }

    if (this.state.confirmContactSharing) {
      contactRole.sharedAttributes = this.state.contactSharedAttributes;
    }

    if (this.state.confirmEmployerSharing) {
      contactRole.employer.sharedAttributes = this.state.employerSharedAttributes;
    }

    this.props.addContactRole(contactRole);
    this.props.onClose();
  }

  render() {
    let body;
    if (this.state.confirmEmployerSharing) {
      body = (
        <SharedAttributeConfirmation
          contact={this.state.employer}
          onChange={attributes => this.setState({ employerSharedAttributes: attributes })}
        />
      );
    } else if (this.state.confirmContactSharing) {
      body = (
        <SharedAttributeConfirmation
          contact={this.state.contact}
          onChange={attributes => this.setState({ contactSharedAttributes: attributes })}
        />
      );
    } else {
      body = this.renderForm();
    }

    return (
      <Modal
        show={this.props.show}
        size="lg"
        onCancel={this.props.onClose}
        onHide={this.props.onClose}
      >
        <Modal.Header closeButton>
          Add {startCase(this.props.contactLabel)}
        </Modal.Header>

        <Modal.Body>
          {body}
        </Modal.Body>

        <Modal.Footer>
          {this.renderControls()}
        </Modal.Footer>
      </Modal>
    );
  }

  renderForm() {
    return (
      <div className="form-vertical-field-container">
        {this.renderContactSelector()}
        {this.state.askForEmployer && this.renderEmployerSelector()}

        {this.props.availableContactRelationships?.length > 1 &&
          <ManagedFormGroup
            label="Role"
            name="role"
            options={this.props.availableContactRelationships}
            placeholder="Choose from the dropdown"
            required
            type="combobox"
            value={this.state.relationship}
            onValueChange={this.handleRelationshipChange}
          />
        }

        {this.props.children}
      </div>
    );
  }

  renderControls() {
    const hasRequiredContact = this.state.contact || !this.props.required;
    const hasRequiredRole = this.state.relationship || !this.props.availableContactRelationships;
    const disabled = !hasRequiredContact || !hasRequiredRole || this.props.invalid;

    const primaryButtonLabel = this.state.confirmContactSharing
      ? 'Confirm'
      : this.props.primaryButtonLabel;

    return <React.Fragment>
      <Button.Cancel name="cancel" onClick={this.props.onClose} />
      <Button.Primary disabled={disabled} name="save" onClick={this.handleSave}>
        {primaryButtonLabel || `Add ${this.props.contactLabel}`}
      </Button.Primary>
    </React.Fragment>;
  }

  renderContactSelector() {
    let label;
    if (this.state.contact?.isBusiness) {
      label = 'Company';
    } else if (this.state.contact?.isPerson) {
      label = 'Person';
    } else {
      label = 'Person or company';
    }

    return (
      <div className="form-group required">
        <label className="form-label">{label}</label>
        <ContactSelector
          allowTagging={this.props.allowTagging}
          contact={this.state.contact}
          modalBackdrop={false}
          multipleBusinesses={this.props.multipleBusinesses}
          newContactFormSections={this.renderNewContactFormSections()}
          newContactVisibility={this.props.newContactVisibility}
          requestPermissions={this.props.requestPermissions}
          validateNewContactFormSections={this.validateNewContactFormSections}
          onChange={this.handleContactChange}
        />
      </div>
    );
  }

  renderEmployerSelector() {
    let input;
    if (this.state.searchForEmployer) {
      input = (
        <ContactSelector
          allowTagging={this.props.allowTagging}
          autoFocus
          contact={this.state.employer}
          contactType="business"
          modalBackdrop={false}
          newContactVisibility={this.props.newContactVisibility}
          requestPermissions={this.props.requestPermissions}
          onChange={employer => this.setState({ employer })}
        />
      );
    } else {
      const businesses = this.state.contact?.businessRecords.map(record => record.business);

      input = (
        <ManagedInput
          getOptionValue={business => business.id}
          isClearable
          options={[...businesses, { id: 'search', label: 'Search for others...' }]}
          type="combobox"
          value={this.state.employer}
          onValueChange={businessOption => this.setState({
            employer: businesses.find(business => business.id === businessOption?.id),
            searchForEmployer: businessOption?.id === 'search',
          })}
        />
      );
    }

    return (
      <div className="form-group">
        <label className="form-label">Company</label>
        {input}
      </div>
    );
  }

  renderNewContactFormSections() {
    if (this.props.askForEmployer || !this.props.availableContactRelationships) {
      return null;
    }

    return (
      <div className="form-vertical-field-container" ref={this.roleRef}>
        <h6>{this.props.parentLabel} information</h6>

        <ManagedFormGroup
          className={classNames({ 'form-group-error': this.state.missingRelationship })}
          feedback={this.state.missingRelationship && 'Role is required'}
          feedbackType="invalid"
          label="Role"
          name="role"
          options={this.props.availableContactRelationships}
          placeholder="Choose from the dropdown"
          required
          type="combobox"
          value={this.state.relationship}
          onValueChange={this.handleRelationshipChange}
        />
      </div>
    );
  }
}

export default GenericContactModal;
